import {
	Component,
	OnInit,
	Output,
	Input,
	EventEmitter,
	AfterViewInit,
	ChangeDetectorRef,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BehaviorSubject, Subject } from "rxjs";
import { map, filter, takeUntil } from "rxjs/operators";
import { Option } from "../../services/models/option.model";
import { AddressService } from "../../services/address.service";
import { Suburb } from "../../services/models/suburb.model";
import { ManualEntryForm } from "./manual-address-entry.model";
import { DodoComponent } from "../../dodo.component";
import { FormFieldEvent, FormFieldEventType } from "@dodo/material-forms";
import { ObjectMappingService } from "../../services/object-mapping.service";
import {
	StreetNumberValidator,
	UnitNumberValidator,
	StreetNameValidator,
} from "@dodo/helper";
// import { QualificationErrorService } from "app/qualification/_core/services/qualification-error.service";
import { QualificationError } from "app/_core/_models/qualification.models";

@Component({
    selector: "dodo-manual-address-entry",
    templateUrl: "./manual-address-entry.component.html",
    styleUrls: ["./manual-address-entry.component.scss"],
    standalone: false
})
export class DodoManualAddressEntryComponent
	extends DodoComponent
	implements OnInit, AfterViewInit {
	// Private Variables
	private __unsubscribeAll: Subject<any>;
	private __formValid = true;
	// Public Variables
	public error: QualificationError;
	// Internal Public Variables
	_form: FormGroup;
	_unitTypes: Option[];
	_streetTypes: Option[];
	_postcodesOb = new BehaviorSubject([]);
	_unitNoRequired = false;
	_validImage = "/assets/img/icons/icon-tick-green.svg";
	_invalidImage = "/assets/img/icons/exclamation-large.svg";
	_showFullSearch = false;
	_appearance = "fill";
	_voidValidImage = false;
	_voidImages = {};
	// Input Variables
	@Input() busy = false;
	@Input() initState: ManualEntryForm.Address;
	// Output Variables
	@Output() submit = new EventEmitter();
	@Output() formStatusUpdated = new EventEmitter();

	// * ----------------------------------------------------------------
	// * @ Constructor
	// * ----------------------------------------------------------------

	constructor(
		private fb: FormBuilder,
		private addressService: AddressService,
		// private __qualificationErrorService: QualificationErrorService,
		private cdr: ChangeDetectorRef
	) {
		super();
		this.__createForm();
		this.__initVoidImages();

		this._form.statusChanges
			.pipe(
				map(() => this.__isFormValid()),
				filter((status) => {
					return status !== this.__formValid;
				})
			)
			.subscribe((status) => {
				this.__formValid = status;
				this.formStatusUpdated.emit(this.__formValid);
			});

		this.__unsubscribeAll = new Subject();
		// this.__qualificationErrorService
		// 	.getQualificationError()
		// 	.pipe(takeUntil(this.__unsubscribeAll))
		// 	.subscribe((err$) => {
		// 		this.error = err$;
		// 	});
	}

	ngAfterViewInit(): void {
		this.cdr.detectChanges();
	}

	// * ----------------------------------------------------------------
	// * @ Implementation of Interface Methods
	// * ----------------------------------------------------------------

	// # OnInit Interface Implementation
	ngOnInit() {
		this.__setupUnitTypes();
		this.__setupStreetTypes();

		if (this._form && this.initState) {
			console.log("manual address entry init state : ", this.initState);
			this._form.patchValue(this.initState);
			Object.keys(this._form.controls).forEach((field) => {
				const control = this._form.get(field);
				control.markAsDirty();
			});
		}
	}

	// * ----------------------------------------------------------------
	// * @ private methods
	// * ----------------------------------------------------------------

	private __initVoidImages() {
		this._voidImages = [
			{
				Name: "unitType",
				Value: false,
			},
			{
				Name: "unitNo",
				Value: false,
			},
			{
				Name: "unitNoSuffix",
				Value: false,
			},
			{
				Name: "streetNo",
				Value: false,
			},
			{
				Name: "streetNoSuffix",
				Value: false,
			},
			{
				Name: "streetName",
				Value: false,
			},
			{
				Name: "streetType",
				Value: false,
			},
			{
				Name: "postcode",
				Value: false,
			},
		];
	}

	private __isFormValid(): boolean {
		let isValid = false;
		if (
			this._form.get("streetNo").value &&
			!this._form.get("streetNo").errors &&
			this._form.get("streetName").value &&
			!this._form.get("streetName").errors &&
			this._form.get("streetType").value &&
			!this._form.get("streetType").errors &&
			this._form.get("suburb").value &&
			this._form.get("state").value &&
			this._form.get("postcode").value &&
			!this._form.get("postcode").errors
		) {
			isValid = true;
		}

		if (
			this._form.get("unitType").value &&
			this._form.get("unitType").value.Key &&
			this._form.get("unitType").value.Key.length > 0
		) {
			if (
				!this._form.get("unitNo").value ||
				this._form.get("unitNo").errors
			) {
				isValid = false;
			}
		}

		return isValid;
	}

	private __createForm() {
		this._form = this.fb.group({
			unitType: [""],
			unitNo: ["", UnitNumberValidator],
			unitNoSuffix: ["", [Validators.maxLength(1), UnitNumberValidator]],
			streetNo: ["", [Validators.required, StreetNumberValidator]],
			streetNoSuffix: [
				"",
				[Validators.maxLength(1), StreetNumberValidator],
			],
			streetName: ["", [Validators.required, StreetNameValidator]],
			streetType: ["", Validators.required],
			suburb: [{ value: "", disabled: true }, Validators.required],
			state: [{ value: "", disabled: true }, Validators.required],
			postcode: ["", [Validators.required]],
		});
	}

	private __setupUnitTypes() {
		this.addressService.unitTypes().subscribe((unitTypes) => {
			this._unitTypes = unitTypes;
		});
	}

	private __setupStreetTypes() {
		this.addressService.streetTypes().subscribe((streetTypes) => {
			this._streetTypes = streetTypes;
			this._streetTypes = this._streetTypes.filter(
				(c) => c.Value !== "Null"
			);
		});
	}

	// * ----------------------------------------------------------------
	// * @ internal public methods
	// * ----------------------------------------------------------------

	_genericAutoCompleteEventHandler(event: FormFieldEvent, fieldNum: number) {
		if (event.type === FormFieldEventType.OPTION_SELECTED) {
			console.log("option selected : ", event.control, event.data);
		}
		this._checkTick(event, fieldNum);
	}

	_unitTypeEventHandler(event: FormFieldEvent) {
		console.log("event : ", event);
		if (event.type === FormFieldEventType.OPTION_SELECTED) {
			const option = <Option>event.data;
			if (!option || option.Key === "" || !this._showFullSearch) {
				this._unitNoRequired = false;
			} else {
				this._unitNoRequired = true;
			}
		}
	}

	_postcodeDisplayFn(option?: Suburb): string | undefined {
		if (!option) {
			return undefined;
		}
		return option.Postcode;
	}

	_postcodeOptionDisplayFn(option?: Suburb): string | undefined {
		if (!option) {
			return undefined;
		}
		return `${option.Suburb} (${option.Postcode}) | ${option.State}`;
	}

	_postcodeAutoCompleteEventHandler(event: FormFieldEvent, fieldNum: number) {
		if (event.type === FormFieldEventType.INPUT_CHANGED) {
			console.log("handling input change");
			this.addressService
				.suburbFromPostcode(event.data)
				.subscribe((suburbs) => {
					console.log("suburbs : ", suburbs);
					this._postcodesOb.next(suburbs);
				});
		}

		if (event.type === FormFieldEventType.OPTION_SELECTED) {
			const suburb = <Suburb>event.data;
			console.log("handling option selected : ", suburb);
			if (suburb) {
				this._form.get("suburb").setValue(suburb.Suburb);
				this._form.get("state").setValue(suburb.State);
			} else {
				this._form.get("suburb").setValue(undefined);
				this._form.get("state").setValue(undefined);
			}
		}

		this._checkTick(event, fieldNum);
	}

	_toggleFullSearch() {
		this._showFullSearch = !this._showFullSearch;
	}

	_checkTick(event: FormFieldEvent, fieldNum: number) {
		if (event.type === FormFieldEventType.INPUT_CHANGED) {
			if (!this._form.get(this._voidImages[fieldNum].Name).value) {
				this._voidImages[fieldNum].Value = true;
			} else {
				this._voidImages[fieldNum].Value = false;
			}
		}
	}

	_handleUnitNoError(error: any) {
		return error.unitNumberInvalid.message;
	}

	_handleStreetNameError(error: any) {
		return error.streetNameInvalid.message;
	}

	_handleStreetNoError(error: any) {
		return error.streetNumberInvalid.message;
	}

	// * ----------------------------------------------------------------
	// * @ public methods
	// * ----------------------------------------------------------------

	public get valid(): boolean {
		return this.__isFormValid();
	}

	public get value(): ManualEntryForm.Address {
		const formData = this._form.getRawValue();
		const transformMap = {
			"unitType.Key": "unitType",
			unitNo: "unitNo",
			unitNoSuffix: "unitNoSuffix",
			streetNo: "streetNo",
			streetNoSuffix: "streetNoSuffix",
			streetName: "streetName",
			"streetType.Key": "streetType",
			"postcode.Postcode": "postcode",
			suburb: "suburb",
			state: "state",
		};

		return ObjectMappingService.transform<ManualEntryForm.Address>(
			formData,
			transformMap
		);
	}

	public doEntry() {
		this.submit.emit(this.value);
	}
}
